import { useEffect, useRef, useState } from 'react';
import {
  Brain, Check, ChevronDown, ChevronRight, CircleDot, Clock, FileCheck2, FilePlus2, FileText, Flag,
  FolderTree, GitCompare, GitMerge, Hash, History, MessageCircleQuestion, Mic, MoveRight, Search, Send,
  Image as ImageIcon, ShieldOff, Trash2, TriangleAlert, Undo2, Wrench, XCircle,
} from 'lucide-react';
import {
  RUN_PHASE_LABEL,
  isTerminalPhase,
  type RunActivity as Activity,
  type RunPhase,
  type RunTimeline,
} from '@repo/shared-types';
import { BreathingDot, BreathingSkeleton, StreamingText, SweepSurface, LiveRail } from '../ui/breathing';
import { Markdown, compactMarkdownComponents } from '../ui/markdown';
import { describeTool } from '../../lib/run-stream';
import { cn } from '../../lib/utils';

/**
 * The run console: what an agent thought, called and decided, in order, live.
 *
 * Shared by both streamed agents. It renders a `RunTimeline`, which is the same object whether the
 * run was a knowledge-base ingestion or an automation — so nothing here branches on which, and the
 * only per-agent knowledge is the tool icon and label tables, both of which are lookups with a
 * fallback. That is the payoff of one event vocabulary: the second agent's live view cost no new UI.
 *
 * Three kinds of entry, and they are visually distinct on purpose — a reader is usually after one of
 * them and not the others. **Thoughts** are prose, set in the reading face, because they are the
 * only part of a run written to be read. **Tool calls** are mechanics, monospaced and collapsed by
 * default, because what matters at a glance is which one is taking the time. **Milestones** are
 * outcomes, and they are what someone opening a finished run is scanning for.
 *
 * The whole thing is one component for live and settled runs. A live run is a settled one with an
 * unfinished tail, and having two components would mean two chances to disagree about the same
 * events.
 */

/**
 * One icon table for both agents' tool rosters — the names don't collide, and a shared table is what
 * lets this component render either kind of run without being told which it has.
 */
const TOOL_ICON: Record<string, typeof Wrench> = {
  // Knowledge-base ingestion.
  kb_outline: FileText,
  kb_search: Search,
  kb_grep: Search,
  kb_read: FileText,
  kb_stage_create: FilePlus2,
  kb_stage_update: FileText,
  kb_stage_delete: Trash2,
  kb_stage_merge: GitMerge,
  kb_stage_move: MoveRight,
  kb_withhold: ShieldOff,
  kb_previous_version: History,
  kb_image: ImageIcon,
  kb_review_plan: Check,
  kb_discard_staged: Trash2,
  kb_commit: Check,
  // Ingestion's placement phase — reads across every project, then assigns.
  ingest_projects: FolderTree,
  ingest_outline: FileText,
  ingest_search: Search,
  ingest_read: FileText,
  ingest_assign: MoveRight,
  ingest_discard: Trash2,
  ingest_withhold: ShieldOff,
  ingest_previous_version: History,
  ingest_image: ImageIcon,
  ingest_review_plan: Check,
  ingest_undo: Trash2,
  ingest_confirm: Check,
  // Automations: gathering, then the hand-offs out.
  current_datetime: Clock,
  kb_ask: MessageCircleQuestion,
  granola_list_notes: Mic,
  granola_read_note: Mic,
  slack_list_messages: Hash,
  automation_previous_runs: History,
  slack_post_message: Send,
  kb_save_note: FilePlus2,
  finish: FileCheck2,
  // The sweep agent: reads over the store, three lenses, and the ledger it writes to.
  sweep_projects: FolderTree,
  sweep_outline: FileText,
  sweep_search: Search,
  sweep_grep: Search,
  sweep_read: FileText,
  sweep_similar_pairs: GitCompare,
  sweep_neighbours: GitCompare,
  sweep_compare: GitCompare,
  sweep_shared_evidence: Hash,
  sweep_open_findings: History,
  sweep_flag: Flag,
  sweep_resolve: Undo2,
  sweep_finish: FileCheck2,
};

const PHASE_TONE: Record<RunPhase, 'accent' | 'ink' | 'good' | 'warn' | 'danger'> = {
  queued: 'ink',
  starting: 'accent',
  thinking: 'accent',
  reading: 'accent',
  triaging: 'accent',
  deciding: 'accent',
  committing: 'accent',
  writing: 'accent',
  delivering: 'accent',
  done: 'good',
  failed: 'danger',
};

/**
 * The header: one line for where the run is, and the specific thing it is doing under it.
 *
 * The phase is a category and the headline is the particular — "Reading the knowledge base" over
 * "Reading engineering/auth/sessions". Both are shown because the category is what tells you whether
 * to keep watching and the particular is what tells you it is not stuck.
 */
export function RunPhaseHeader({
  timeline,
  headline,
  live,
}: {
  timeline: RunTimeline;
  headline: { phase: RunPhase; line: string };
  live: boolean;
}) {
  const phase = timeline.phase;
  const moving = live && !isTerminalPhase(phase);
  const chunks = timeline.chunkCount ?? 0;

  return (
    <SweepSurface active={moving} className="rounded-xl border border-border bg-surface-subtle px-4 py-3.5">
      <div className="flex items-start gap-3">
        {moving ? (
          <BreathingDot size={11} tone={PHASE_TONE[phase]} className="mt-1" />
        ) : phase === 'failed' ? (
          <XCircle size={13} className="mt-0.5 flex-shrink-0 text-danger-text" />
        ) : (
          <Check size={13} className="mt-0.5 flex-shrink-0 text-emerald-500" />
        )}

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">
            {isTerminalPhase(phase) ? RUN_PHASE_LABEL[phase] : RUN_PHASE_LABEL[headline.phase]}
          </p>
          <p className="mt-0.5 truncate text-xs text-ink-muted">
            {headline.line || (moving ? 'Getting started' : '—')}
          </p>
        </div>

        {chunks > 1 && (
          <span className="flex-shrink-0 rounded-full bg-surface-muted px-2 py-0.5 text-[10px] font-medium text-ink-muted">
            part {Math.max(1, timeline.chunk)} / {chunks}
          </span>
        )}
      </div>

      {timeline.error && (
        <p className="mt-2.5 rounded-md border border-danger-ring bg-danger-bg px-2.5 py-1.5 text-[11px] text-danger-text">
          {timeline.error}
        </p>
      )}
    </SweepSurface>
  );
}

export function RunTimelineView({
  timeline,
  live,
  loading,
}: {
  timeline: RunTimeline;
  live: boolean;
  loading: boolean;
}) {
  const endRef = useRef<HTMLDivElement | null>(null);
  const [pinned, setPinned] = useState(true);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  /**
   * Follow the tail while the reader is already at it, and stop the moment they scroll up.
   *
   * A live log that yanks you back down while you are reading something further up is worse than one
   * that never follows at all, so the pin is released by any upward scroll and taken again only by
   * returning to the bottom.
   */
  useEffect(() => {
    if (!pinned) return;
    endRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [timeline.activities.length, timeline.cursor, pinned]);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setPinned(el.scrollHeight - el.scrollTop - el.clientHeight < 48);
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-border bg-card p-4">
        <BreathingSkeleton lines={4} />
      </div>
    );
  }

  if (timeline.activities.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border px-4 py-9 text-center">
        {live ? (
          <>
            <BreathingDot size={10} className="mx-auto mb-3" />
            <p className="text-xs text-ink-muted">Waiting for the agent's first move…</p>
          </>
        ) : (
          <p className="text-xs text-ink-faint">No activity was recorded for this run.</p>
        )}
      </div>
    );
  }

  return (
    <div
      ref={scrollerRef}
      onScroll={onScroll}
      className="max-h-[26rem] overflow-y-auto rounded-xl border border-border bg-card"
    >
      <div className="relative space-y-0.5 py-2">
        {/* Inside the content wrapper, not the scroller: `inset-y-0` against a scroll *container*
            resolves to one viewport's height, so the rail would end mid-list and scroll away. The
            wrapper is as tall as the content, which is what makes the thread continuous. */}
        <LiveRail live={live} className="left-[1.4rem]" />

        {timeline.activities.map((activity, i) => (
          <ActivityRow
            key={activity.key}
            activity={activity}
            chunkChanged={i > 0 && activity.chunk !== timeline.activities[i - 1].chunk}
            multiChunk={(timeline.chunkCount ?? 1) > 1}
          />
        ))}
      </div>
      <div ref={endRef} />
    </div>
  );
}

function ActivityRow({
  activity,
  chunkChanged,
  multiChunk,
}: {
  activity: Activity;
  chunkChanged: boolean;
  multiChunk: boolean;
}) {
  return (
    <>
      {multiChunk && chunkChanged && (
        <p className="mb-1 mt-2 pl-12 text-[10px] font-semibold uppercase tracking-[0.07em] text-ink-faint">
          Part {activity.chunk}
        </p>
      )}
      {activity.kind === 'thought' ? (
        <ThoughtRow activity={activity} />
      ) : activity.kind === 'tool' ? (
        <ToolRow activity={activity} />
      ) : (
        <MilestoneRow activity={activity} />
      )}
    </>
  );
}

/**
 * The model's own words. Set as prose, at reading width, with the highlight still travelling while
 * the block is unfinished — the one place in the app where text is arriving rather than displayed.
 *
 * A finished block is rendered as the markdown it is. Models write these in markdown whether or not
 * anyone renders it, so a run read back was full of literal `**`, `##` and `1.` — worst of all in the
 * closing summary, which is the part of a run people actually read.
 *
 * **While it is still arriving it stays plain text**, and that is not a shortcut. Half-streamed
 * markdown is not markdown: an unclosed `**` is literal until its pair lands, a table is a stray
 * pipe until its second row, and a heading appears the instant its `#` does — so rendering the tail
 * live means blocks reflowing under the reader's eye as they resolve. It would also cost the one
 * thing the streaming state has: the travelling highlight paints a gradient clipped to the text and
 * needs the text transparent, which every rendered element's own colour would immediately overpaint.
 * So the caret and the shimmer belong to the text as it types, and the typography arrives with the
 * full stop — which is also the state every replayed run is in, since only completed blocks are
 * stored.
 */
function ThoughtRow({ activity }: { activity: Extract<Activity, { kind: 'thought' }> }) {
  const streaming = !activity.done;
  return (
    <div className="cdr-rise flex gap-3 px-4 py-2">
      <span className="relative z-10 mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-card">
        <Brain size={12} className={streaming ? 'text-orange-500' : 'text-ink-faint'} />
      </span>
      <div className="min-w-0 flex-1">
        {activity.thoughtKind === 'reasoning' && (
          <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-[0.07em] text-ink-faint">Reasoning</p>
        )}
        {streaming ? (
          <StreamingText
            text={activity.text}
            streaming
            className="text-[12.5px] leading-relaxed text-ink-secondary"
          />
        ) : (
          // Trimmed because a block often ends with a newline the model used as punctuation, and an
          // empty trailing paragraph is a gap in the timeline with nothing in it.
          <Markdown components={compactMarkdownComponents}>{activity.text.trim()}</Markdown>
        )}
      </div>
    </div>
  );
}

/**
 * One tool call. Collapsed to a line, because most of them are steps toward something rather than
 * the something — but expandable, since the argument and the result are the evidence behind whatever
 * the run ended up doing.
 */
function ToolRow({ activity }: { activity: Extract<Activity, { kind: 'tool' }> }) {
  const [open, setOpen] = useState(false);
  const Icon = TOOL_ICON[activity.tool] ?? Wrench;
  const running = activity.state === 'running';
  const bad = activity.state === 'error' || activity.state === 'refused';

  return (
    <div className="cdr-rise">
      <SweepSurface active={running}>
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex w-full items-center gap-3 px-4 py-1.5 text-left transition-colors hover:bg-surface-subtle"
        >
          <span className="relative z-10 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-card">
            {running ? (
              <BreathingDot size={8} />
            ) : bad ? (
              <TriangleAlert size={11} className="text-amber-500" />
            ) : (
              <Icon size={11} className="text-ink-faint" />
            )}
          </span>

          <span className={cn('truncate text-xs', running ? 'font-medium text-ink' : 'text-ink-secondary')}>
            {describeTool(activity.tool, activity.input)}
          </span>

          <span className="ml-auto flex flex-shrink-0 items-center gap-2">
            {activity.ms !== undefined && !running && (
              <span className="text-[10px] tabular-nums text-ink-faint">{formatMs(activity.ms)}</span>
            )}
            <span className="font-mono text-[10px] text-ink-faint">{activity.tool}</span>
            {open ? (
              <ChevronDown size={11} className="text-ink-faint" />
            ) : (
              <ChevronRight size={11} className="text-ink-faint" />
            )}
          </span>
        </button>
      </SweepSurface>

      {open && (
        <div className="space-y-2 bg-surface-subtle px-4 py-2.5 pl-12">
          <Payload label="Asked for" value={activity.input} />
          <Payload label="Got back" value={activity.output} />
        </div>
      )}
    </div>
  );
}

/** An outcome: staged, refused, committed, a page changed. The line a finished run is read for. */
function MilestoneRow({ activity }: { activity: Extract<Activity, { kind: 'milestone' }> }) {
  const warn = activity.tone === 'warn';
  return (
    <div className="cdr-rise flex gap-3 px-4 py-2">
      <span className="relative z-10 mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-card">
        {warn ? (
          <TriangleAlert size={11} className="text-amber-500" />
        ) : (
          <CircleDot size={11} className="text-emerald-500" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className={cn('truncate text-xs font-medium', warn ? 'text-amber-600 dark:text-amber-400' : 'text-ink')}>
          {activity.label}
        </p>
        {activity.detail && (
          <p className="mt-0.5 text-[11px] leading-relaxed text-ink-muted">{activity.detail}</p>
        )}
      </div>
    </div>
  );
}

function Payload({ label, value }: { label: string; value: unknown }) {
  if (value === undefined || value === null) return null;
  return (
    <div>
      <p className="mb-1 text-[10px] uppercase tracking-wide text-ink-faint">{label}</p>
      <pre className="whitespace-pre-wrap break-words font-mono text-[11px] text-ink-secondary">
        {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

function formatMs(ms: number): string {
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}
